import {
  Button,
  CircularProgress,
  Dialog,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { useLastVisible } from '~/shared/hooks/useLastVisible';
import {
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogPaper,
} from '~/modules/matters/modals/shared';
import { ModalHeader } from '~/shared/components/ModalHeader';
import { useToast } from '~/shared/contexts/ToastContext';
import type { RetainerFieldsFragment } from '~/gql/graphql';
import { useRetainerMutations } from '~/modules/matters/useRetainerMutations';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  request: RetainerFieldsFragment | null;
  onDeclined?: () => void;
}

const DeclineRetainerDialog = ({
  isOpen,
  onClose,
  matterId,
  request: requestProp,
  onDeclined,
}: Props) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { showToast } = useToast();
  const { declineRetainer, isDecliningRetainer } = useRetainerMutations();
  const [message, setMessage] = useState('');

  const request = useLastVisible(requestProp, isOpen);

  const handleClose = () => {
    if (isDecliningRetainer) {
      return;
    }
    setMessage('');
    onClose();
  };

  const handleDecline = async () => {
    if (!request) {
      return;
    }
    try {
      await declineRetainer(matterId, request.id, message.trim() || undefined);
      showToast('Request declined. The expert has been notified.');
      onDeclined?.();
      setMessage('');
      onClose();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'decline_retainer_request' },
        extra: { matterId, retainerRequestId: request.id },
      });
      showToast('Failed to decline request. Please try again.', 'error');
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={fullScreen}
      PaperComponent={StyledDialogPaper}
    >
      <ModalHeader title="Decline retainer request" onClose={handleClose} />
      <StyledDialogContent>
        {request ? (
          <Stack gap={2}>
            <Typography variant="body2" color="text.secondary">
              You are declining the retainer request for{' '}
              {request.hoursRequested ?? '0'} hrs
              {request.title ? `: ${request.title}` : ''}. Optionally add a
              message for the expert.
            </Typography>
            <TextField
              label="Message (optional)"
              placeholder="e.g. Scope doesn't fit our needs at this time."
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              multiline
              minRows={2}
              fullWidth
              variant="outlined"
              size="medium"
            />
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No retainer request selected.
          </Typography>
        )}
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledDeclineButton
          onClick={handleDecline}
          color="error"
          variant="contained"
          disabled={!request || isDecliningRetainer}
          endIcon={
            isDecliningRetainer ? (
              <CircularProgress size={20} color="inherit" />
            ) : undefined
          }
        >
          Decline request
        </StyledDeclineButton>
      </StyledDialogActions>
    </Dialog>
  );
};

const StyledDeclineButton = styled(Button)(({ theme }) => ({
  minHeight: 44,
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
}));

export default DeclineRetainerDialog;
